// src/services/deliveryApprovalHold.ts
// -----------------------------------
// Verified-address model (docs/delivery-address-verification-plan.md), Batch 5.
// A DISPATCHED item — a Device (laptop) or a Stationery Pack (a fee of type
// "Stationery") — must not be APPROVED while the request's delivery address is
// unverified (delivery_address_verified === false). Zephyr ships those two items,
// so approving one authorises dispatch to an address a responder hasn't checked.
// Money-only fees (Textbooks, Camp, Resource, etc.) and reimbursement uniforms are
// not dispatched by Zephyr, so they never gate. Unlike the monitored-shelter hold,
// there is NO admin exemption — it's a data-integrity gate, not an escalation.
//
// Pure logic; UI wiring lives in ApprovalDecisions (Confirm & Send backstop) and,
// later, ResponderChildView (per-selection block).

import { APPROVAL_STATUSES } from "../utils/enums";

export const DELIVERY_UNVERIFIED_MESSAGE =
  "This request's delivery address is pending Zephyr verification. Use the " +
  '"Verify address" button on this request (next to the delivery address) to ' +
  "confirm it before approving a Device or Stationery Pack for dispatch.";

// db-146/147 promotion model: a BANNED delivery address is a do-not-use address.
// It hard-blocks approval of a dispatched item and — unlike the unverified hold —
// CANNOT be cleared by verifying. The responder must re-route (Edit Address → a
// verified, non-banned address) or a supervisor must remove the ban.
export const DELIVERY_BANNED_MESSAGE =
  "This request's delivery address is BANNED (do not use). Change the delivery " +
  'address with the "Edit Address" button (choose a verified address), or ask a ' +
  "supervisor to remove the ban, before approving a Device or Stationery Pack.";

// The Stationery-Pack fee is the fee whose type is "Stationery" (QLD packs
// fulfilled by the Zephyr warehouse). Case/space-insensitive.
export function isStationeryPackFeeType(feeType: unknown): boolean {
  return (
    String(feeType ?? "")
      .trim()
      .toLowerCase() === "stationery"
  );
}

function isApproved(status: unknown): boolean {
  return status === APPROVAL_STATUSES.APPROVED;
}

// Per-selection block REASON: the message to show (or null) when setting `status`
// to Approved on a dispatched item (a device, or a stationery-pack fee). BANNED
// takes priority over unverified. Only Approved is blocked — Declined / Needs Info
// authorise no dispatch.
export function deliveryApprovalBlockReason(args: {
  deliveryAddressVerified: boolean | null | undefined;
  deliveryAddressBanned?: boolean | null | undefined;
  isDispatchItem: boolean;
  status: unknown;
}): string | null {
  if (!args.isDispatchItem) return null;
  if (!isApproved(args.status)) return null;
  if (args.deliveryAddressBanned === true) return DELIVERY_BANNED_MESSAGE;
  if (args.deliveryAddressVerified === false) return DELIVERY_UNVERIFIED_MESSAGE;
  return null;
}

// Back-compat boolean wrapper (blocked for any reason). Prefer
// deliveryApprovalBlockReason at call sites so the shown message matches the cause.
export function isDeliveryApprovalBlocked(args: {
  deliveryAddressVerified: boolean | null | undefined;
  deliveryAddressBanned?: boolean | null | undefined;
  isDispatchItem: boolean;
  status: unknown;
}): boolean {
  return deliveryApprovalBlockReason(args) !== null;
}

interface HoldChild {
  fees?: { feeType?: string; approvalStatus?: string }[] | null;
  laptop?: { approvalStatus?: string } | null;
}

// Confirm & Send backstop: true if ANY Device or Stationery-Pack item is Approved
// across the children tree. Gate it behind `deliveryAddressVerified === false` at
// the call site so it only fires for an unverified linked address.
export function hasApprovedDispatchItem(children: HoldChild[] | null | undefined): boolean {
  if (!Array.isArray(children)) return false;
  return children.some((c) => {
    if (c.laptop && isApproved(c.laptop.approvalStatus)) return true;
    if (
      (c.fees || []).some(
        (f) => isStationeryPackFeeType(f?.feeType) && isApproved(f?.approvalStatus)
      )
    )
      return true;
    return false;
  });
}

// Confirm & Send backstop REASON: the message to show (or null) when confirming a
// request that has an approved dispatch item but a banned/unverified delivery
// address. Banned takes priority. Combines hasApprovedDispatchItem with the
// delivery flags so the call site is a single check.
export function confirmDispatchBlockReason(args: {
  deliveryAddressVerified: boolean | null | undefined;
  deliveryAddressBanned?: boolean | null | undefined;
  children: HoldChild[] | null | undefined;
}): string | null {
  if (!hasApprovedDispatchItem(args.children)) return null;
  if (args.deliveryAddressBanned === true) return DELIVERY_BANNED_MESSAGE;
  if (args.deliveryAddressVerified === false) return DELIVERY_UNVERIFIED_MESSAGE;
  return null;
}
